import { createContext, PropsWithChildren, useCallback, useContext, useState } from "react";

import { EMAIL_DRAFT_CARRYING_ROUTES } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { buildUrlWithQuery } from "@/common/helpers/router";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { EmailEditorDrawer } from "@/components/EmailEditor/EmailEditorDrawer";

interface EmailEditorContextProps {
  openEmailEditorDrawer: (emailContentId: string, thankYouTransactionId?: string) => void;
  closeEmailEditorDrawer: () => void;
  getThankYouEmailFromCache: (transactionId: string) => string | undefined;
  saveThankYouEmailToCache: (transactionId: string, body: string) => void;
  isEmailEditorOpen: boolean;
}

const EmailEditorContext = createContext<EmailEditorContextProps>({
  closeEmailEditorDrawer: () => {},
  openEmailEditorDrawer: () => {},
  getThankYouEmailFromCache: () => undefined,
  saveThankYouEmailToCache: () => {},
  isEmailEditorOpen: false,
});

export const EmailEditorProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const params = useSearchParams();
  const query = Object.fromEntries(params.entries());

  const emailContentId = params.get("emailContentId") as string;
  const thankYouTransactionId = params.get("thankYouTransactionId") as string;
  const pathname = router.pathname;
  const carriesDraftThroughFlow = EMAIL_DRAFT_CARRYING_ROUTES.some((route) => pathname.includes(route));
  const isEmailEditorOpen = (Boolean(emailContentId) || Boolean(thankYouTransactionId)) && !carriesDraftThroughFlow;

  // Cache for AI-generated thank you emails, keyed by transactionId
  const [thankYouEmailCache, setThankYouEmailCache] = useState<Record<string, string>>({});

  const getThankYouEmailFromCache = useCallback(
    (transactionId: string) => {
      return thankYouEmailCache[transactionId];
    },
    [thankYouEmailCache]
  );

  const saveThankYouEmailToCache = useCallback((transactionId: string, body: string) => {
    setThankYouEmailCache((prev) => ({ ...prev, [transactionId]: body }));
  }, []);

  const openEmailEditorDrawer = useCallback(
    async (emailContentId: string, thankYouTransactionId?: string) => {
      // Use asPath (not pathname) to keep real dynamic segments instead of `[param]`.
      // We rebuild the URL manually to merge query params while preserving hash fragments, then push it.
      const nextUrl = buildUrlWithQuery(router.asPath, {
        ...query,
        emailContentId,
        ...(thankYouTransactionId ? { thankYouTransactionId } : {}),
      });
      router.push(nextUrl);
    },
    [query, router]
  );

  const closeEmailEditorDrawer = useCallback(() => {
    const { emailContentId: _emailContentId, thankYouTransactionId: _thankYouTransactionId, ...newQuery } = query;
    const nextUrl = buildUrlWithQuery(router.asPath, newQuery);
    router.push(nextUrl);
  }, [query, router]);

  return (
    <EmailEditorContext.Provider
      value={{
        openEmailEditorDrawer,
        closeEmailEditorDrawer,
        getThankYouEmailFromCache,
        saveThankYouEmailToCache,
        isEmailEditorOpen,
      }}
    >
      {children}
      <EmailEditorDrawer
        emailContentId={emailContentId}
        closeEmailEditor={closeEmailEditorDrawer}
        isOpen={isEmailEditorOpen}
      />
    </EmailEditorContext.Provider>
  );
};

export const useEmailEditorContext = () => useContext(EmailEditorContext);
